import { UrlApi } from "../../../services/apirest";
import { cerrarSesionSiTokenInvalido } from "../../../services/services";

const buildQuery = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === "") return;
    qs.set(key, String(value));
  });
  const query = qs.toString();
  return query ? `?${query}` : "";
};

const authHeaders = (extra = {}) => {
  const token = localStorage.getItem("token");
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const parseJsonSafe = async (res) => {
  try {
    return await res.json();
  } catch {
    return {};
  }
};

const throwIfError = (res, data, fallback) => {
  if (!res.ok || data?.ok === false) {
    if (res.status === 401) {
      cerrarSesionSiTokenInvalido(data?.codigo);
    }
    const error = new Error(
      data?.msg || data?.message || data?.error || fallback
    );
    error.status = res.status;
    error.codigo = data?.codigo ?? null;
    throw error;
  }
};

/** Traduce mensajes técnicos del backend a textos cortos para el usuario. */
const mensajeErrorAmigable = (msg, fallback) => {
  const raw = String(msg || "").trim();
  if (!raw) return fallback;
  const lower = raw.toLowerCase();
  if (lower.includes("unexpected field") || lower.includes("unexpected file")) {
    return "No se pudo subir el archivo. Probá de nuevo.";
  }
  if (lower.includes("file too large") || lower.includes("too large")) {
    return "El archivo es demasiado grande.";
  }
  if (lower.includes("too many files")) {
    return "Solo se puede subir un archivo a la vez.";
  }
  if (
    /^(unexpected|forbidden|unauthorized|multipart|payload|enoent|econn)/i.test(
      raw
    )
  ) {
    return fallback;
  }
  return raw;
};

export const getCheques = async (filtros = {}) => {
  const {
    estado,
    // físicos van por /cheques-fisicos; eCheq no los mezcla
    electronico = 1,
    cuit_recibido,
    fecha_pago_desde,
    fecha_pago_hasta,
    q,
    bandeja,
    origen_cheque,
    solo_endosables,
    limit = 50,
    offset = 0,
  } = filtros;

  const query = buildQuery({
    estado,
    electronico,
    cuit_recibido,
    fecha_pago_desde,
    fecha_pago_hasta,
    q,
    bandeja,
    origen_cheque,
    ...(solo_endosables ? { solo_endosables: 1 } : {}),
    limit,
    offset,
  });

  const res = await fetch(`${UrlApi}/cheques${query}`, {
    headers: authHeaders(),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudieron obtener los cheques");
  return data;
};

export const getChequesResumen = async () => {
  const res = await fetch(`${UrlApi}/cheques/resumen`, {
    headers: authHeaders(),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudo obtener el resumen de cheques");
  return data;
};

export const getChequeById = async (id, { origen_cheque } = {}) => {
  const query = buildQuery({ origen_cheque });
  const res = await fetch(`${UrlApi}/cheques/${id}${query}`, {
    headers: authHeaders(),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudo obtener el cheque");
  return data;
};

export const importarCheques = async (file, confirmar = false) => {
  if (!file) {
    throw new Error("Seleccione un archivo CSV o Excel");
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("user", localStorage.getItem("user") || "");

  const res = await fetch(
    `${UrlApi}/cheques/importar?confirmar=${confirmar ? 1 : 0}&completo=1`,
    {
      method: "POST",
      headers: authHeaders(),
      body: formData,
    }
  );
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudo procesar el archivo de cheques");
  return data;
};

/** PATCH /cheques/:id/revision-cc — decision: aceptar | pendiente | repudiar | reabrir (alias rechazar). */
export const revisionCc = async (id, body) => {
  const res = await fetch(`${UrlApi}/cheques/${id}/revision-cc`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body ?? {}),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudo registrar la revisión de CC");
  return data;
};

/** POST /cheques/revision-cc/lote — misma decision que revision-cc (preferida: repudiar). */
export const revisionCcLote = async (body) => {
  const res = await fetch(`${UrlApi}/cheques/revision-cc/lote`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body ?? {}),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudo registrar la revisión de CC en lote");
  return data;
};

/** PATCH /cheques/:id/baja — solo desde aceptado. Si está pendiente_cc/pendiente, el back responde 409. */
export const bajaCheque = async (id, body = {}) => {
  const res = await fetch(`${UrlApi}/cheques/${id}/baja`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body ?? {}),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudo dar de baja el cheque");
  return data;
};

export const importarAceptados = async (
  file,
  { confirmar = false, marcarFaltantes = false } = {}
) => {
  if (!file) {
    throw new Error("Seleccione un archivo CSV o Excel");
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("user", localStorage.getItem("user") || "");

  const query = buildQuery({
    confirmar: confirmar ? 1 : 0,
    marcar_faltantes: marcarFaltantes ? 1 : 0,
    completo: 1,
  });

  const res = await fetch(`${UrlApi}/cheques/importar-aceptados${query}`, {
    method: "POST",
    headers: authHeaders(),
    body: formData,
  });
  const data = await parseJsonSafe(res);
  throwIfError(
    res,
    data,
    "No se pudo procesar el archivo de aceptados del banco"
  );
  return data;
};

/** @deprecated Finanzas acepta vía importar-aceptados. El backend responde 410. */
export const aceptacionFinanzas = async (id, body) => {
  const res = await fetch(`${UrlApi}/cheques/${id}/aceptacion-finanzas`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body ?? {}),
  });
  const data = await parseJsonSafe(res);
  throwIfError(
    res,
    data,
    "La aceptación de Finanzas se hace importando el Excel de aceptados del banco"
  );
  return data;
};

/** @deprecated Finanzas acepta vía importar-aceptados. El backend responde 410. */
export const aceptacionFinanzasLote = async (body) => {
  const res = await fetch(`${UrlApi}/cheques/aceptacion-finanzas/lote`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body ?? {}),
  });
  const data = await parseJsonSafe(res);
  throwIfError(
    res,
    data,
    "La aceptación de Finanzas se hace importando el Excel de aceptados del banco"
  );
  return data;
};

export const pasarACartera = async (id, body = {}) => {
  const res = await fetch(`${UrlApi}/cheques/${id}/pasar-a-cartera`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body ?? {}),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudo pasar el cheque a cartera");
  return data;
};

export const definirDestino = async (id, body) => {
  const res = await fetch(`${UrlApi}/cheques/${id}/destino`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body ?? {}),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudo definir el destino del cheque");
  return data;
};

/** Proveedores que matchean el CUIT del cheque (recibido). */
export const buscarProveedoresPorCuit = async (cuit) => {
  const cuitNorm = String(cuit || "").replace(/\D/g, "");
  if (cuitNorm.length < 8) return [];

  const res = await fetch(`${UrlApi}/proveedores/bycuit/${cuitNorm}`, {
    headers: authHeaders(),
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      data?.msg ||
        data?.message ||
        data?.error ||
        "No se pudieron buscar proveedores por CUIT"
    );
  }

  const lista = Array.isArray(data) ? data : data ? [data] : [];
  return lista.filter(Boolean).map((p) => ({
    ...p,
    id: p.id ?? p.id_proveedor,
    razon_social: p.razon_social || p.nombre || p.proveedor || "",
    cuit: p.cuit || p.nro_cuit || cuitNorm,
  }));
};

/** Búsqueda libre de proveedores (por CUIT o texto vía bycuit si son dígitos). */
export const buscarProveedoresCheques = async (texto) => {
  const q = String(texto || "").trim();
  const digitos = q.replace(/\D/g, "");
  if (digitos.length >= 8) {
    return buscarProveedoresPorCuit(digitos);
  }
  if (q.length < 2) return [];

  // Fallback: intentar bycuit solo si parece CUIT; si no, sin endpoint de search genérico
  return [];
};

const normalizarCliente = (c, cuitNorm = "") => ({
  ...c,
  id: c.id ?? c.id_cliente,
  razon_social: c.razon_social || c.nombre || c.cliente || "",
  cuit: c.cuit || c.nro_cuit || cuitNorm,
});

/** Clientes que matchean el CUIT del cheque. */
export const buscarClientesPorCuit = async (cuit) => {
  const cuitNorm = String(cuit || "").replace(/\D/g, "");
  if (cuitNorm.length < 8) return [];

  const res = await fetch(`${UrlApi}/clientes/existecuit/${cuitNorm}`, {
    headers: authHeaders(),
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      data?.msg ||
        data?.message ||
        data?.error ||
        "No se pudieron buscar clientes por CUIT"
    );
  }

  const lista = Array.isArray(data) ? data : data ? [data] : [];
  return lista.filter(Boolean).map((c) => normalizarCliente(c, cuitNorm));
};

/** Búsqueda de clientes por CUIT o texto. */
export const buscarClientesCheques = async (texto) => {
  const q = String(texto || "").trim();
  const digitos = q.replace(/\D/g, "");
  if (digitos.length >= 8) {
    return buscarClientesPorCuit(digitos);
  }
  if (q.length < 2) return [];

  const res = await fetch(
    `${UrlApi}/clientes/search?q=${encodeURIComponent(q)}`,
    { headers: authHeaders() }
  );
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      data?.msg ||
        data?.message ||
        data?.error ||
        "No se pudieron buscar clientes"
    );
  }
  const lista = Array.isArray(data) ? data : data ? [data] : [];
  return lista.filter(Boolean).map((c) => normalizarCliente(c));
};

let cacheMotivosRechazo = null;
let inflightMotivosRechazo = null;

/** Normaliza GET /cheques/motivos-rechazo → [{ codigo, motivo }]. */
export const extraerMotivosRechazo = (data) => {
  const raw = Array.isArray(data?.motivos)
    ? data.motivos
    : Array.isArray(data)
      ? data
      : [];
  return raw
    .map((m) => {
      if (typeof m === "string") return null;
      const codigoNum = Number(m?.codigo ?? m?.value ?? m?.id);
      const codigo =
        Number.isFinite(codigoNum) && codigoNum > 0 ? codigoNum : null;
      const motivo = String(
        m?.motivo ?? m?.label ?? m?.nombre ?? m?.descripcion ?? ""
      ).trim();
      if (!motivo || codigo == null) return null;
      return { codigo, motivo };
    })
    .filter(Boolean);
};

/** Catálogo estático. Se cachea en memoria; no hace falta invalidar. */
export const getMotivosRechazo = async () => {
  if (cacheMotivosRechazo) return cacheMotivosRechazo;
  if (inflightMotivosRechazo) return inflightMotivosRechazo;

  inflightMotivosRechazo = (async () => {
    try {
      const res = await fetch(`${UrlApi}/cheques/motivos-rechazo`, {
        headers: authHeaders(),
      });
      const data = await parseJsonSafe(res);
      throwIfError(res, data, "No se pudieron cargar los motivos de rechazo");
      const motivos = extraerMotivosRechazo(data);
      cacheMotivosRechazo = motivos;
      return motivos;
    } finally {
      inflightMotivosRechazo = null;
    }
  })();

  return inflightMotivosRechazo;
};

export const postAltaRechazo = async (idCheque, body = {}) => {
  const res = await fetch(`${UrlApi}/cheques/${idCheque}/rechazo`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body ?? {}),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudo registrar el rechazo");
  return data;
};

export const getRechazos = async (filtros = {}) => {
  const {
    bandeja,
    estado,
    origen,
    q,
    limit = 50,
    offset = 0,
  } = filtros;

  const query = buildQuery({
    bandeja,
    estado,
    origen,
    q,
    limit,
    offset,
  });

  const res = await fetch(`${UrlApi}/cheques/rechazos${query}`, {
    headers: authHeaders(),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudieron obtener los rechazos");
  return data;
};

export const getRechazoById = async (idRechazo) => {
  const res = await fetch(`${UrlApi}/cheques/rechazos/${idRechazo}`, {
    headers: authHeaders(),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudo obtener el rechazo");
  return data;
};

export const patchFinanzasNd = async (idRechazo, body = {}) => {
  const res = await fetch(
    `${UrlApi}/cheques/rechazos/${idRechazo}/finanzas-nd`,
    {
      method: "PATCH",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body ?? {}),
    }
  );
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudo confirmar la nota de débito");
  return data;
};

export const patchPagosRechazo = async (idRechazo, body = {}) => {
  const res = await fetch(`${UrlApi}/cheques/rechazos/${idRechazo}/pagos`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body ?? {}),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudo registrar la gestión de Pagos");
  return data;
};

export const patchJefeRechazo = async (idRechazo, body = {}) => {
  const res = await fetch(`${UrlApi}/cheques/rechazos/${idRechazo}/jefe`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body ?? {}),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudo formalizar el rechazo");
  return data;
};

export const patchCcRechazo = async (idRechazo, body = {}) => {
  const res = await fetch(`${UrlApi}/cheques/rechazos/${idRechazo}/cc`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body ?? {}),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudo cerrar el rechazo en Cuentas Corrientes");
  return data;
};

export const listArchivosRechazo = async (idRechazo) => {
  const res = await fetch(`${UrlApi}/cheques/rechazos/${idRechazo}/archivos`, {
    headers: authHeaders(),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudieron listar los archivos del rechazo");
  return data;
};

export const uploadArchivoRechazo = async (idRechazo, file, tipo = "otro") => {
  if (!file) {
    throw new Error("Seleccione un archivo");
  }
  const formData = new FormData();
  formData.append("archivo", file);
  formData.append("tipo", tipo || "otro");
  formData.append("user", localStorage.getItem("user") || "");

  const res = await fetch(`${UrlApi}/cheques/rechazos/${idRechazo}/archivos`, {
    method: "POST",
    headers: authHeaders(),
    body: formData,
  });
  const data = await parseJsonSafe(res);
  try {
    throwIfError(res, data, "No se pudo subir el archivo");
  } catch (err) {
    throw new Error(
      mensajeErrorAmigable(err.message, "No se pudo subir el archivo")
    );
  }
  return data;
};

export const downloadArchivoRechazo = async (idRechazo, idArchivo, nombre) => {
  const res = await fetch(
    `${UrlApi}/cheques/rechazos/${idRechazo}/archivos/${idArchivo}/download`,
    { headers: authHeaders() }
  );
  if (!res.ok) {
    const data = await parseJsonSafe(res);
    throw new Error(
      data?.msg || data?.message || data?.error || "No se pudo descargar el archivo"
    );
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre || `archivo-${idArchivo}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/** Menús del usuario (system_menu_auth). Authz de cheques = rutas asignadas. */
export const getMenuesUsuario = async (username) => {
  const user = username || localStorage.getItem("user");
  if (!user) return [];

  const res = await fetch(`${UrlApi}/menues/${user}`, {
    headers: authHeaders(),
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      data?.msg || data?.message || data?.error || "No se pudieron obtener los menús"
    );
  }
  return Array.isArray(data) ? data : [];
};
